// The phone accounts-list sparkline (§s01): plain SVG bars, no recharts --
// it is 18 bars at most. Actual points are neutral chart bars; gap points
// are a hatched bar of full height, never a zero-height (or zero-value) bar.
import { useMoneyFormat } from '@/lib/money';
import { ariaTableFor, type ChartPoint } from './chartMasking';

export function Sparkline({
  points,
  height = 36,
  className,
}: {
  points: ChartPoint[];
  height?: number;
  className?: string;
}) {
  const { currency, masked } = useMoneyFormat();
  const values = points.map((p) => (p.value !== null ? Math.abs(Number(p.value)) : null));
  const max = Math.max(1, ...values.filter((v): v is number => v !== null));
  const table = ariaTableFor(points, masked, currency);

  return (
    <div className={className} data-testid="sparkline" role="img" aria-label="Balance sparkline">
      <div className="flex h-9 items-end gap-[3px]" style={{ height }} aria-hidden="true">
        {points.map((p, i) => {
          const isGap = p.kind === 'gap' || p.value === null;
          const pct = isGap ? 100 : Math.max(6, Math.round(((values[i] ?? 0) / max) * 100));
          return (
            <span
              key={p.date}
              data-testid="sparkline-bar"
              data-kind={p.kind}
              className={['flex-1 rounded-sm', isGap ? 'ms-hatch' : 'bg-gap'].join(' ')}
              style={{ height: `${pct}%` }}
            />
          );
        })}
      </div>
      {/* The accessible table must not affect layout. `sr-only` on a
          `display:table` element does not shrink it -- a table ignores a
          1px width and expands to min-content, and an absolutely
          positioned overflow still widens the page (it pushed /accounts/trend
          past the viewport at 320px). The wrapper does the clipping. */}
      <div className="sr-only">
      <table>
        <caption>{table.caption}</caption>
        <tbody>
          {table.rows.map((row) => (
            <tr key={row.date}>
              <th scope="row">{row.date}</th>
              <td>{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

export default Sparkline;
